import type { Reservation, Share, SharedView } from '@closetly/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { pb } from './pb';

/*
 * Share links need the server anyway (the link is only worth something once
 * it exists there), so they're written directly instead of through the
 * offline queue in queries.ts.
 */

export const sharesKey = (uid: string) => ['shares', uid] as const;

export function useShares(uid: string) {
  return useQuery({
    queryKey: sharesKey(uid),
    queryFn: () => pb.collection('shares').getFullList<Share>({ sort: '-created' }),
  });
}

export type ShareFields = Partial<Pick<Share, 'board' | 'outfit' | 'show_prices' | 'show_notes' | 'show_reservations' | 'active'>>;

export function useShareActions(uid: string) {
  const qc = useQueryClient();
  const refresh = useCallback(() => qc.invalidateQueries({ queryKey: sharesKey(uid) }), [qc, uid]);
  return {
    create: async (fields: ShareFields) => {
      const s = await pb.collection('shares').create<Share>({ owner: uid, active: true, ...fields });
      await refresh();
      return s;
    },
    /** Shown right away; a failed save comes back with the refetch. */
    update: async (id: string, fields: ShareFields) => {
      qc.setQueryData<Share[]>(sharesKey(uid), (list) => list?.map((s) => (s.id === id ? { ...s, ...fields } : s)));
      try {
        return await pb.collection('shares').update<Share>(id, fields);
      } finally {
        await refresh();
      }
    },
  };
}

/** Who reserved what on a link; the server only answers if the owner switched it on. */
export function useReservations(shareId: string | null) {
  return useQuery({
    queryKey: ['reservations', shareId],
    queryFn: () =>
      pb.collection('reservations').getFullList<Reservation>({
        filter: pb.filter('share = {:id}', { id: shareId }),
        sort: 'created',
      }),
    enabled: !!shareId,
  });
}

// ---- visitors ---------------------------------------------------------------

export function loadSharedView(token: string) {
  return pb.send<SharedView>(`/api/closetly/share/${encodeURIComponent(token)}`, {});
}

export function reservePiece(token: string, item: string, name: string) {
  return pb.send<{ key: string }>(`/api/closetly/share/${encodeURIComponent(token)}/reserve`, {
    method: 'POST',
    body: { item, name },
  });
}

export function releasePiece(token: string, item: string, key: string) {
  return pb.send(`/api/closetly/share/${encodeURIComponent(token)}/release`, { method: 'POST', body: { item, key } });
}

/**
 * The keys of this browser's own reservations, so it can take them back.
 * Lost with the browser storage; then the reservation stays until the
 * owner buys the piece.
 */
const myKey = (token: string) => `closetly-reserved:${token}`;

export function myReservations(token: string): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(myKey(token)) ?? '{}');
  } catch {
    return {};
  }
}

export function rememberReservation(token: string, item: string, key: string | null) {
  try {
    const all = myReservations(token);
    if (key) all[item] = key;
    else delete all[item];
    localStorage.setItem(myKey(token), JSON.stringify(all));
  } catch {
    // private mode: the reservation still counts, it just can't be taken back here
  }
}
